/**
 * Bond Palantir Configuration
 *
 * Centralized configuration file that reads from environment variables.
 * Uses Create React App's REACT_APP_ prefix convention.
 *
 * For local development: Create .env.local with your settings
 * For production: Set environment variables or use .env.production
 *
 * See .env.example for all available configuration options
 */

// Base URL for API (HTTP)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Base URL for WebSocket (WS)
const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL;

const config = {
  // API Configuration
  api: {
    baseUrl: API_BASE_URL,
    // Dashboard API endpoints
    dashboardApi: `${API_BASE_URL}/api`,
    dashboardUsers: `${API_BASE_URL}/api/users`,
    dashboardBonds: `${API_BASE_URL}/api/bonds`,
    dashboardLogs: `${API_BASE_URL}/api/dashboard/logs`,
    dashboardUserLogs: `${API_BASE_URL}/api/dashboard/user-logs`,
    // Palantir API endpoints
    palantirApi: `${API_BASE_URL}/palantir/api/`,
  },

  // WebSocket Configuration
  websocket: {
    baseUrl: WS_BASE_URL,

    /**
     * Get WebSocket URL for dashboard bond events
     * @param {string} bondId - Bond ID
     * @returns {string} WebSocket URL
     */
    getDashboardBondEventsUrl: (bondId) => {
      return `${WS_BASE_URL}/ws/dashboard/bonds/${bondId}/`;
    },

    getDashboardUserEventsUrl: (userId) => {
      return `${WS_BASE_URL}/ws/dashboard/users/${userId}/`;
    },

    /**
     * Get WebSocket URL for Palantir livelog
     * @param {string} bondId - Bond ID
     * @param {string} token - Bond token (optional)
     * @returns {string} WebSocket URL
     */
    getPalantirLivelogUrl: (bondId, token = '') => {
      return `${WS_BASE_URL}/ws/palantir/${bondId}/?token=${token}`;
    },
  },

  // Log Server Configuration (separate service)
  logs: {
    serverUrl: process.env.REACT_APP_LOG_SERVER_URL,

    /**
     * Get log server URL for a specific bond
     * @param {string} bondId - Bond ID
     * @returns {string} Log server URL
     */
    getBondLogsUrl: (bondId) => {
      return `${process.env.REACT_APP_LOG_SERVER_URL}/${bondId}`;
    },
  },

  // AWS CloudWatch Configuration
  aws: {
    cloudwatchLogsBaseUrl: process.env.REACT_APP_CLOUDWATCH_LOGS_BASE_URL,
  },

  // S3 Updates Configuration
  updates: {
    s3BaseUrl: process.env.REACT_APP_S3_UPDATES_BASE_URL,

    /**
     * Get versions JSON URL for firmware updates
     * @param {string} fwTarget - Firmware target (e.g., 'zappy', 'xappy')
     * @param {string} branchPath - Branch path (e.g., 'release', 'develop')
     * @returns {string} Versions JSON URL
     */
    getVersionsUrl: (fwTarget, branchPath) => {
      return `${process.env.REACT_APP_S3_UPDATES_BASE_URL}/${fwTarget}/${branchPath}/versions_internal.json`;
    },
  },

  // Authentication Configuration
  auth: {
    // OAuth2 proxy login endpoint (legacy)
    loginUrl: `${API_BASE_URL}/oauth2/sign_in`,
    // OAuth2 proxy logout endpoint (legacy)
    logoutUrl: `${API_BASE_URL}/oauth2/sign_out`,
    // User info endpoint (returns current user details)
    userInfoUrl: `${API_BASE_URL}/api/auth/me`,

    // AWS Cognito OIDC Configuration
    cognito: {
      authority: process.env.REACT_APP_COGNITO_AUTHORITY,
      client_id: process.env.REACT_APP_COGNITO_CLIENT_ID,
      redirect_uri: process.env.REACT_APP_COGNITO_REDIRECT_URI || window.location.origin,
      response_type: 'code',
      // Default to standard OIDC scopes that work with Google federation
      scope: process.env.REACT_APP_COGNITO_SCOPE || 'openid email profile',
      post_logout_redirect_uri: process.env.REACT_APP_COGNITO_LOGOUT_URI || window.location.origin,
      // Cognito domain for logout
      cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN,
    },
  },

  // Backup Configuration
  backup: {
    // IP address sent to the Bond in the sys/backup payload (Bond cannot resolve DNS)
    hostIp: process.env.REACT_APP_BACKUP_HOST_IP,
    // Plain-HTTP port exposed by nginx for Bond backup uploads (no TLS, no auth)
    hostPort: process.env.REACT_APP_BACKUP_HOST_PORT || '10184',
  },

  // Livelog Configuration (UDP streaming)
  livelog: {
    // IP address of the livelog UDP server
    ip: process.env.REACT_APP_LIVELOG_IP || '23.22.42.239',
    // UDP port for livelog streaming
    port: parseInt(process.env.REACT_APP_LIVELOG_PORT) || 5000,
    // Encryption key for livelog (must match backend PALANTIR_LIVELOG_ENCRYPTION_KEY).
    // Wired through docker-compose: PALANTIR_LIVELOG_ENCRYPTION_KEY → REACT_APP_LIVELOG_ENCRYPTION_KEY build arg.
    // No fallback — if the env var is unset at build time, livelog decryption will fail loudly
    // rather than silently using a known-public default.
    key: process.env.REACT_APP_LIVELOG_ENCRYPTION_KEY,
  },

  // Feature Flags
  features: {
    enableDebug: process.env.REACT_APP_ENABLE_DEBUG === 'true',
    enableMateDebug: process.env.REACT_APP_ENABLE_MATE_DEBUG === 'true',
  },

  // Environment Info
  env: {
    isDevelopment: process.env.NODE_ENV === 'development',
    isProduction: process.env.NODE_ENV === 'production',
    isTest: process.env.NODE_ENV === 'test',
  },
};

// Freeze config to prevent accidental modifications
Object.freeze(config);
Object.freeze(config.api);
Object.freeze(config.websocket);
Object.freeze(config.logs);
Object.freeze(config.aws);
Object.freeze(config.updates);
Object.freeze(config.auth);
Object.freeze(config.auth.cognito);
Object.freeze(config.backup);
Object.freeze(config.livelog);
Object.freeze(config.features);
Object.freeze(config.env);

export default config;
